import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import he from "he";
import "@chatscope/chat-ui-kit-styles/dist/default/styles.min.css";
import '../style/chat.css'
import {
  ChatContainer,
  MainContainer,
  Message as ChatMessage,
  MessageList,
  ConversationHeader,
  Avatar,
  Sidebar,
  ConversationList,
  Conversation,
  MessageSeparator,
} from "@chatscope/chat-ui-kit-react";
import ChatInput from "./ChatInput";
import { useNavigate } from "react-router-dom";
import { isArray, isEmpty } from "underscore";
import { DateTime } from "luxon";
import { LuClock3 } from "react-icons/lu";
import useBodyClasses from "../hooks/useBodyClasses";
import { useChatManager } from "../hooks/useChatManager";
import OrderMessage from "./OrderMessage";
import FileMessage from "./FileMessage";
import { IoCheckmark, IoCheckmarkDone, IoCloseOutline } from "react-icons/io5";
import avatarImg from "../assets/avatar.jpg";
import { CloseCircle } from "iconsax-react";
import {
  chatState, appStore,
  chatPopupStore,
  userStore,
  usePresenceStore,
} from "../zustand";
import { InstantDb } from "../zustand/instant.schema";
import { Box, IconButton, InputAdornment, TextField, Typography, useMediaQuery, useTheme } from "@mui/material";
import { CiSearch } from 'react-icons/ci';
import { useAdminRoomManager } from '../hooks/useAdminRoomManager';
import { useRoomManager } from '../hooks/useRoomManager';
import useDebounce from '../components/Debounce';
import { ChatMode } from '../types/chatmode.enum';
import { AppNameSpaces, AppRooms } from '../types/app-rooms.enum';
import ConversationSummary from './Conversation';


function ChatEngine({ mode = ChatMode.FULL, useBackBtn = true, dm = {} }) {
  const navigate = useNavigate();
  const theme = useTheme();
  const appMode = appStore((state) => state.mode);
  const setOpenChatBox = chatState((state) => state.setOpenChatBox);
  const openChatBox = chatState((state) => state.openChatBox);
  const [firstName, setFirstName] = useState('No');
  const [lastName, setLastName] = useState('Name');
  const [photo, setPhoto] = useState(avatarImg);
  const user = userStore((state) => state.user);
  const setShowPopup = chatPopupStore((state) => state.setShow);
  const mainContainerRef = useRef(null);
  const [mainContainerWidth, setMainContainerWidth] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const isMobileScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [showSearch, setShowSearch] = useState(false);
  const [search, setSearch] = useState('');
  const { messages } = useChatManager();
  const { roomId, setRoomId, roomInfo: room, setRoomInfo } = chatState();
  const { rooms: dms, isLoading: isLoadingDms } = useAdminRoomManager()
  const { getRoomById } = useRoomManager()
  const peers = usePresenceStore((state) => state.presence);

  const debouncedSearch = useDebounce(search, 300);

  useBodyClasses(['chatmode']);

  const messageContainerRef = useRef(null);
  const setSearchDms = chatState((state) => state.setSearchDms);

  useMemo(() => {
    setSearchDms(debouncedSearch);
  }, [debouncedSearch]);

  const updateMessageReadStatus = async (messageId) => {
    try {
      const msg = messages.filter((m) => m.id === messageId)[0];

      if (String(user.id) === String(msg.sender)) return;
      await InstantDb.transact(
        InstantDb.tx.messages[messageId].update({
          isRead: true,
          updatedAt: JSON.stringify(new Date()),
        })
      );
    } catch (error) {
      console.error("Error updating message read status:", error);
    }
  };

  useEffect(() => {
    if (room && 'user' in room) {
      const userData = room.user;
      setFirstName(userData?.firstName ?? 'No');
      setLastName(userData?.lastName ?? 'Name');
      setPhoto(userData?.photoUrl ?? avatarImg);
    }
  }, [room]);

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const messageId = entry.target.getAttribute('data-message-id');
          // const isRead = entry.target.getAttribute('data-read');
          if (!messageId) return;
          const msg = messages.filter((m) => m.id === messageId)[0];
          if (msg.isRead === false) {
            updateMessageReadStatus(messageId);
          }
          observer.unobserve(entry.target);
        }
      });
    });

    const messageContainer = document.querySelector('.chat-box');
    if (messageContainer) {
      const messageElements = messageContainer.querySelectorAll('[data-message-id]');
      messageElements.forEach(element => observer.observe(element));
    }
    return () => observer.disconnect();
  }, [messages]);


  const getDirection = (senderType) => {
    let direction = "outgoing";
    switch (appMode) {
      case "admin":
        direction = senderType === "customer" ? "incoming" : "outgoing";
        break;
      case "customer":
        direction = senderType !== "customer" ? "incoming" : "outgoing";
        break;
      default:
        direction = "outgoing";
    }
    return direction;
  };

  const setActiveDm = async (dm) => {
    setOpenChatBox(true)
    setRoomId(dm.id)
    let room = await getRoomById(dm.id)
    setRoomInfo(room);
  }

  const handleReachStart = () => {

  }
  const localTime = (createdAt) => {
    if (!createdAt) {
      return "";
    }
    try {
      const isoDate = new Date(JSON.parse(createdAt));
      const iso = isoDate.toISOString();

      if (isNaN(Date.parse(iso))) {
        console.error("Invalid Date:", createdAt);
        return "Invalid date";
      }

      // Parse the ISO string in UTC
      const dateTime = DateTime.fromISO(iso, { zone: "utc" });

      // Check if Luxon's DateTime object is valid
      if (!dateTime.isValid) {
        console.error("Invalid DateTime:", dateTime.invalidReason);
        return "Invalid date";
      }
      const formattedTime = dateTime.toLocal().toFormat("hh:mm a");

      return formattedTime;
    } catch (error) {
      console.error("Error parsing date:", error);
      return "Invalid date";
    }
  };

  const renderMessage = (data, index) => {
    const getStatus = (msg) => {
      if (msg.isRead) {
        return <IoCheckmarkDone />
      } else if (msg.isSaved) {
        return <IoCheckmark />
      } else {
        return <LuClock3 />
      }
    }
    const { messageType } = data;
    switch (messageType) {
      case 'order':
        return (
          <div as="Message2" key={index}>
            <OrderMessage data={data} />
          </div>
        );
      case 'image':
      case 'video':
      case 'document':
        return (
          <ChatMessage
            className="file-message"
            key={index}
            model={{
              type: "custom",
              sender: `${data.sender}`,
              direction: getDirection(data.senderType),
            }}
            data-message-id={data.id}
            data-read={data.isRead}
          >
            <ChatMessage.CustomContent>
              <FileMessage message={data} />
              <ChatMessage.Footer className="msg-footer flex justify-between items-center">
                <span>{localTime(data.createdAt)}</span>
                <span className="w-4 h-4 flex items-center justify-center font-bold delivery">
                  {getStatus(data)}
                </span>
              </ChatMessage.Footer>
            </ChatMessage.CustomContent>
          </ChatMessage>
        );
      default:
        return <ChatMessage
          className="chat"
          key={index}
          payload={data.content}
          model={{
            message: data.content,
            sentTime: "just now",
            sender: data.senderType,
            direction: getDirection(data.senderType),
          }}
          data-message-id={data.id}
          data-read={data.isRead}
        >
          <ChatMessage.CustomContent>
            {he.decode(data?.content)}
          </ChatMessage.CustomContent>
          <ChatMessage.Footer className="flex justify-between items-center">
            <span>{localTime(data.createdAt)}</span>
            <span className="w-4 h-4 flex items-center justify-center font-bold delivery">
              {getStatus(data)}
            </span>
          </ChatMessage.Footer>
        </ChatMessage>
    }
  };

  const validateMessage = (data) => {
    if (!data) return false;
    const { content, messageType } = data;
    if (!messageType) return false;
    if (messageType === 'text' && isEmpty(content)) return false;
    return true;
  };

  const handleReachEnd = () => { };

  const handleBack = () => {
    if (appMode !== 'customer') {
      setOpenChatBox(false)
    } else {
      navigate(-1)
    }
  }

  const roomInstance = InstantDb.room(AppRooms.CHAT, roomId)
  const { active } = InstantDb.rooms.useTypingIndicator(
    roomInstance,
    AppNameSpaces.CHAT_INPUT,
  );

  const getInfo = () => {
    let isCustomerOnline = peers && isArray(peers) && room ? peers.filter((p) => p.email == room.customer) : ''

    if (active.length > 0) {
      return "typing";
    }
    if (appMode == 'admin' && isCustomerOnline.length > 0) {
      return "online";
    }

    return "";
  };

  useEffect(() => {
    if (isMobileScreen || mainContainerWidth < 768) {
      setIsMobile(true);
    } else {
      setIsMobile(false);
    }
  }, [isMobileScreen, mainContainerWidth])

  useLayoutEffect(() => {
    if (mainContainerRef.current) {
      setMainContainerWidth(mainContainerRef.current.offsetWidth);
    }
  }, []);

  useEffect(() => {
    const updateWidth = () => {
      if (mainContainerRef.current) {
        setMainContainerWidth(mainContainerRef.current.offsetWidth);
      }
    };

    const resizeObserver = new ResizeObserver(updateWidth);

    if (mainContainerRef.current) {
      resizeObserver.observe(mainContainerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const renderStatus = (data) => {
    return ''
  };

  // Helper to group messages by date with custom labels
  function groupMessagesByDate(messages) {
    const groups = {};
    const now = DateTime.local();
    const yesterday = now.minus({ days: 1 });
    messages.forEach(msg => {
      const dt = DateTime.fromISO(JSON.parse(msg.createdAt));
      let label;
      if (dt.hasSame(now, 'day')) {
        label = 'Today';
      } else if (dt.hasSame(yesterday, 'day')) {
        label = 'Yesterday';
      } else if (dt.hasSame(now, 'week')) {
        label = dt.toFormat('cccc'); // Weekday name
      } else {
        label = dt.toFormat('d LLLL yyyy'); // e.g., 5 April 2025
      }
      if (!groups[label]) groups[label] = [];
      groups[label].push(msg);
    });
    return groups;
  }

  return (
    <>
      <Box className='chat-wrapper' ref={mainContainerRef} style={{ flex: 1 }}>
        <MainContainer style={{ flex: 1 }} responsive={true} className={`chat-engine ${mode ? mode : 'full'}`}>
          {appMode === 'admin' && mode !== ChatMode.DM && ((isMobile && !openChatBox) || !isMobile) && <>
            <Sidebar position="left" className={isMobile ? 'use-full-width' : ''}>
              <Box className="chat-list-header" sx={{
                padding: '1rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                {!showSearch && (
                  <Typography variant='h6'>Chats</Typography>
                )}
                {showSearch && (
                  <form style={{ width: '100%' }} action="#" onSubmit={(e) => {
                    e.preventDefault();
                    // refetch();
                  }}>
                    <TextField
                      placeholder="Enter project name"
                      size='small'
                      fullWidth
                      color='secondary'
                      variant='standard'
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      sx={{ width: '100%', borderRadius: '1rem', my: 2 }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <IconButton size='small'>
                              <CiSearch />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </form>
                )}
                {showSearch && <>
                  <IconButton onClick={() => {
                    setShowSearch(false)
                    setSearch('')
                  }}>
                    <IoCloseOutline />
                  </IconButton>
                </>}
                {!showSearch && (
                  <IconButton onClick={() => setShowSearch(true)}>
                    <CiSearch />
                  </IconButton>
                )}
              </Box>
              {!isEmpty(dms) &&
                <ConversationList
                  loading={isLoadingDms}
                  // loadingMore={isFetchingNextPageDms}
                  onYReachEnd={handleReachEnd}
                  style={{
                    height: "auto",
                    flex: 1,
                    overflowY: "auto",
                  }}
                >
                  {dms && dms.length > 0 && (
                    dms
                      .filter(dm => !isEmpty(dm) && 'name' in dm && !isEmpty(dm.name))
                      .map((data, index) => {
                        if (!data) return null;
                        return (
                          <ConversationSummary
                            key={index}
                            index={index}
                            room={data}
                            setActiveDm={setActiveDm}
                          />
                        );
                      })
                  )}

                </ConversationList>
              }
            </Sidebar>
          </>}
          {((!isMobile || openChatBox) || appMode === 'customer') &&
            <ChatContainer className="chat-box">
              {Boolean(mode !== ChatMode.DM) &&
                <ConversationHeader>
                  <ConversationHeader.Back onClick={handleBack} />
                  <Avatar
                    name={
                      appMode === 'customer'
                        ? "Agent"
                        : `${firstName} ${lastName}`
                    }
                    src={
                      appMode === 'customer'
                        ? avatarImg
                        : photo ?? avatarImg
                    }
                  />
                  <ConversationHeader.Content
                    info={getInfo()}
                    userName={
                      appMode === 'customer'
                        ? "Doshby"
                        : `${room?.name ?? 'No Name'}`
                    }
                  />
                  {useBackBtn && <>
                    <ConversationHeader.Actions>
                      <button onClick={() => setShowPopup(false)} className="btn p-0 btn-ghost text-secondary hover:bg-transparent hover:text-secondary-content">
                        <CloseCircle size="24" color="#999" />
                      </button>
                    </ConversationHeader.Actions>
                  </>}
                </ConversationHeader>
              }
              <MessageList
                onYReachEnd={handleReachEnd}
                onYReachStart={handleReachStart}
                scrollBehavior="auto"
                ref={messageContainerRef}
                // autoScrollToBottomOnMount={true}
                loadingMorePosition="top"
                autoScrollToBottom={true}
              >
                {messages &&
                  !isEmpty(messages) &&
                  isArray(messages) &&
                  (() => {
                    // Filter and group messages
                    const validMessages = messages.filter(validateMessage);
                    const grouped = groupMessagesByDate(validMessages);
                    const groupLabels = Object.keys(grouped);
                    let rendered = [];
                    groupLabels.forEach((label) => {
                      rendered.push(
                        <MessageSeparator key={`sep-${label}`}>{label}</MessageSeparator>
                      );
                      grouped[label].forEach((msg, idx) => {
                        rendered.push(renderMessage(msg, `${label}-${idx}`));
                      });
                    });
                    return rendered;
                  })()}
              </MessageList>
              <Box component={'div'} as="MessageInput" sx={{
                px: '0.5rem',
                mx: 'auto',
                maxWidth: '98%',
                [theme.breakpoints.up("sm")]: {
                  maxWidth: '90%',
                  px: '1rem',
                },
                [theme.breakpoints.down("xs")]: {
                },
              }} className="max-w-[100vw] msg-input">
                <ChatInput instanceMode={mode} />
              </Box>
            </ChatContainer>
          }
        </MainContainer>
      </Box >
    </>
  );
}

export default ChatEngine;